import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

interface LoadingContextType {
  isLoading: boolean;
  loadingStates: Record<string, boolean>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

// Helper functions for better readability
const isValidSlice = (slice: any): boolean => {
  return slice && typeof slice === 'object';
};

const isLoadingProperty = (key: string, value: any): boolean => {
  return key.toLowerCase().includes('loading') && typeof value === 'boolean';
};

const extractLoadingStatesFromSlice = (slice: any, sliceKey: string): Record<string, boolean> => {
  const loadingStates: Record<string, boolean> = {};
  
  Object.entries(slice).forEach(([key, value]) => {
    if (isLoadingProperty(key, value)) {
      loadingStates[`${sliceKey}.${key}`] = value as boolean;
    }
  });
  
  return loadingStates;
};

export const LoadingProvider = ({ children }: LoadingProviderProps) => {
  
  const allLoadingStates = useSelector((state: RootState) => {
    const allLoadingStates: Record<string, boolean> = {};
    
    Object.entries(state).forEach(([sliceKey, slice]) => {
      if (!isValidSlice(slice)) return;
      
      const sliceLoadingStates = extractLoadingStatesFromSlice(slice, sliceKey);
      Object.assign(allLoadingStates, sliceLoadingStates);
    });
    
    return allLoadingStates;
  });

  const isLoading = useMemo(() => {
    return Object.values(allLoadingStates).some(Boolean);
  }, [allLoadingStates]);

  return (
    <LoadingContext.Provider value={{ 
      isLoading, 
      loadingStates: allLoadingStates 
    }}>
      {children}
      
      <Backdrop
        sx={{
          color: '#d40029',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};